import { useState, useEffect, useCallback } from 'react';
import {
  HorarioFormState, HORARIO_FORM_INITIAL, HorarioPayload, DiaSemana, TipoHorario,
} from '../types/horario.types';
import { AsignacionItem } from '../types/matricula.types';
import { AñoEscolar, Grado, Seccion } from '../types/usuario.types';
import { horarioService, asignacionesTodasService } from '../services/horarioService';
import { catalogoService } from '../services/usuarioService';

// ============================================================
// useHorarioForm – creación y edición de horarios (CLASE y RECREO)
// ============================================================

export interface HorarioFormErrors { [field: string]: string }

// HH:mm:ss  →  HH:mm  (para el input type="time")
const toTimeInput = (t: string) => t.substring(0, 5);

// HH:mm  →  HH:mm:ss  (payload que espera el backend)
const toTimePayload = (t: string) => `${t}:00`;

export const useHorarioForm = (idHorario: number | null, onSuccess: () => void) => {
  const isEditing = idHorario !== null;

  const [form, setForm]               = useState<HorarioFormState>(HORARIO_FORM_INITIAL);
  const [errors, setErrors]           = useState<HorarioFormErrors>({});
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loadingInit, setLoadingInit] = useState(false);

  // Catálogos para CLASE
  const [asignaciones,        setAsignaciones]        = useState<AsignacionItem[]>([]);
  const [loadingAsignaciones, setLoadingAsignaciones] = useState(true);
  const [errorAsignaciones,   setErrorAsignaciones]   = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Catálogos para RECREO
  const [añosEscolares, setAñosEscolares] = useState<AñoEscolar[]>([]);
  const [todosGrados,   setTodosGrados]   = useState<Grado[]>([]);
  const [todasSecciones, setTodasSecciones] = useState<Seccion[]>([]);

  // Cargar asignaciones con timeout de 10 s
  useEffect(() => {
    const ac    = new AbortController();
    const timer = setTimeout(() => ac.abort(), 10_000);

    setLoadingAsignaciones(true);
    setErrorAsignaciones(false);

    asignacionesTodasService.listar(ac.signal)
      .then(data => { setAsignaciones(data); setErrorAsignaciones(false); })
      .catch(() => setErrorAsignaciones(true))
      .finally(() => { setLoadingAsignaciones(false); clearTimeout(timer); });

    return () => { ac.abort(); clearTimeout(timer); };
  }, [retryCount]);

  // Cargar catálogos de RECREO (grados, secciones, años)
  useEffect(() => {
    Promise.all([
      catalogoService.getAñosEscolares(),
      catalogoService.getGrados(),
      catalogoService.getSecciones(),
    ]).then(([años, grados, secciones]) => {
      setAñosEscolares(años);
      setTodosGrados(grados);
      setTodasSecciones(secciones);
    }).catch(() => {});
  }, []);

  // Cargar datos en modo edición
  const loadHorario = useCallback(async () => {
    if (!isEditing || idHorario === null) return;
    setLoadingInit(true);
    try {
      const data = await horarioService.obtener(idHorario);
      setForm({
        tipo:                  data.tipo ?? 'CLASE',
        asignacionAcademicaId: data.asignacionAcademicaId != null ? String(data.asignacionAcademicaId) : '',
        gradoId:               '',   // el response no devuelve gradoId separado para RECREO
        seccionId:             '',
        anioEscolarId:         '',
        dia:                   data.dia,
        horaInicio:            toTimeInput(data.horaInicio),
        horaFin:               toTimeInput(data.horaFin),
      });
    } catch { /* mantiene vacío */ }
    finally { setLoadingInit(false); }
  }, [idHorario, isEditing]);

  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    setSubmitError(null);
    // Al cambiar el grado, limpiar la sección para evitar inconsistencias
    if (name === 'gradoId') {
      setForm(prev => ({ ...prev, gradoId: value, seccionId: '' }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  }, []);

  const setDia = useCallback((dia: DiaSemana) => {
    setErrors(prev => { const n = { ...prev }; delete n.dia; return n; });
    setForm(prev => ({ ...prev, dia }));
  }, []);

  const setTipo = useCallback((tipo: TipoHorario) => {
    setErrors({});
    setSubmitError(null);
    setForm(prev => ({ ...prev, tipo, asignacionAcademicaId: '', gradoId: '', seccionId: '', anioEscolarId: '' }));
  }, []);

  const validate = (): HorarioFormErrors => {
    const e: HorarioFormErrors = {};
    if (!form.horaInicio) e.horaInicio = 'Ingresa la hora de inicio';
    if (!form.horaFin)    e.horaFin    = 'Ingresa la hora de fin';
    if (form.horaInicio && form.horaFin && form.horaInicio >= form.horaFin)
      e.horaFin = 'La hora de fin debe ser posterior a la de inicio';

    if (form.tipo === 'CLASE') {
      if (!form.asignacionAcademicaId) e.asignacionAcademicaId = 'Selecciona una asignación';
      if (!form.dia)                   e.dia                   = 'Selecciona un día';
    } else {
      if (!form.anioEscolarId) e.anioEscolarId = 'Selecciona el año escolar';
      if (!form.gradoId)       e.gradoId       = 'Selecciona el grado';
      if (!form.seccionId)     e.seccionId     = 'Selecciona la sección';
    }
    return e;
  };

  const handleSubmit = useCallback(async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSubmitting(true);
    setSubmitError(null);
    try {
      let payload: HorarioPayload;
      if (form.tipo === 'RECREO') {
        // RECREO: sin campo dia; el back crea un registro por cada día de la semana
        payload = {
          tipo:          'RECREO',
          gradoId:       Number(form.gradoId),
          seccionId:     Number(form.seccionId),
          anioEscolarId: Number(form.anioEscolarId),
          horaInicio:    toTimePayload(form.horaInicio),
          horaFin:       toTimePayload(form.horaFin),
        };
      } else {
        payload = {
          tipo:                  'CLASE',
          asignacionAcademicaId: Number(form.asignacionAcademicaId),
          dia:                   form.dia,
          horaInicio:            toTimePayload(form.horaInicio),
          horaFin:               toTimePayload(form.horaFin),
        };
      }

      if (isEditing && idHorario) {
        await horarioService.modificar(idHorario, payload);
      } else {
        await horarioService.crear(payload);
      }
      onSuccess();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar el horario');
    } finally {
      setSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isEditing, idHorario]);

  const reintentarAsignaciones = useCallback(() => setRetryCount(c => c + 1), []);

  // Secciones filtradas según el grado seleccionado.
  // Si ninguna sección tiene gradoId definido (campo no devuelto por la API),
  // se muestran todas para no dejar la lista vacía.
  const seccionesPorGrado = form.gradoId
    ? todasSecciones.filter(s => s.gradoId === Number(form.gradoId))
    : todasSecciones;
  const seccionesFiltradas = seccionesPorGrado.length > 0 ? seccionesPorGrado : todasSecciones;

  return {
    form, errors, submitting, submitError, loadingInit,
    asignaciones, loadingAsignaciones, errorAsignaciones, reintentarAsignaciones,
    añosEscolares, todosGrados, seccionesFiltradas,
    handleChange, setDia, setTipo, handleSubmit, loadHorario,
  };
};
